import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { FileText, Volume2, Copy, Check, Sparkles, CheckCircle2, Image as ImageIcon, Send, Mic } from 'lucide-react';
import { KadmiLogo } from './KadmiLogo';

const V = '#7FB342';

// Stages durations (ms)
const STAGES = [
  { id: 'upload',  duration: 2800 },
  { id: 'analyzing', duration: 2200 },
  { id: 'result',  duration: 4500 },
  { id: 'listen',  duration: 2800 },
  { id: 'copied',  duration: 2200 }
];

const DEMO_DATA = {
  summary: "La CAF vous notifie un trop-perçu de 487 € à rembourser sous 2 mois.",
  explanation: "Vous pouvez contester cette décision en demandant une remise de dette ou un échéancier de remboursement.",
  actions: [
    "Demander une remise gracieuse dans les 2 mois",
    "Envoyer vos justificatifs de ressources",
    "Solliciter un échéancier adapté"
  ],
  letter: "Madame, Monsieur,\n\nSuite à votre notification de trop-perçu en date du [DATE], je sollicite une remise gracieuse de cette dette conformément à l'article L.553-2 du Code de la sécurité sociale..."
};

const PulseRing = () => (
  <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(30,91,224,0.25)' }} />
);

const ChatbotDemo = memo(() => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [stageIdx, setStageIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [soundBars] = useState(() => [0,1,2,3,4]);

  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const ts = isDark ? '#9998B8' : '#6B6B8A';
  const cardBg = isDark ? '#1A1A2E' : '#FFFFFF';
  const bd = isDark ? '#2E2E45' : '#E5E3F0';
  const bgInner = isDark ? 'rgba(30,91,224,0.08)' : 'rgba(30,91,224,0.05)';
  const bdInner = isDark ? 'rgba(30,91,224,0.2)' : 'rgba(30,91,224,0.15)';

  useEffect(() => {
    const current = STAGES[stageIdx];
    const timeout = setTimeout(() => {
      setStageIdx((s) => (s + 1) % STAGES.length);
    }, current.duration);
    return () => clearTimeout(timeout);
  }, [stageIdx]);

  useEffect(() => {
    if (STAGES[stageIdx].id === 'copied') {
      setCopied(true);
    } else if (STAGES[stageIdx].id === 'upload') {
      setCopied(false);
    }
  }, [stageIdx]);

  const stage = STAGES[stageIdx].id;

  return (
    <section className="py-16 px-4 lb-section" data-testid="chatbot-demo-section">
      <div className="lb-content max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ background: isDark ? 'rgba(30,91,224,0.12)' : 'rgba(30,91,224,0.08)', color: V, border: `1px solid ${bdInner}` }}>
            <Sparkles className="h-3 w-3" /> Démo en direct
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl mb-2" style={{ color: tp }}>Photo → Analyse → Courrier prêt</h2>
          <p className="text-sm max-w-xl mx-auto" style={{ color: ts }}>
            Prenez votre courrier en photo, écoutez l'explication, copiez la réponse. En 30 secondes.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-6 md:gap-4">
          {/* LEFT: Phone mockup */}
          <div className="flex justify-center">
            <div className="relative w-full max-w-[340px] rounded-[36px] p-3 shadow-2xl" style={{ background: isDark ? '#0B0F1A' : '#1A1A2E', border: `6px solid ${isDark ? '#1A1A2E' : '#0B0F1A'}` }}>
              <div className="rounded-[28px] overflow-hidden" style={{ background: cardBg, minHeight: 520 }}>
                {/* Chat header */}
                <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${bd}` }}>
                  <KadmiLogo size={32} />
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{ color: tp }}>Kadmi</p>
                    <p className="text-[9px] flex items-center gap-1" style={{ color: ts }}>
                      <span className="w-1 h-1 rounded-full bg-green-500 inline-block" />En ligne
                    </p>
                  </div>
                </div>

                {/* Chat content */}
                <div className="px-3 py-3 space-y-2" style={{ minHeight: 420 }}>
                  <AnimatePresence mode="wait">
                    {stage === 'upload' && (
                      <motion.div key="upload" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex justify-end">
                        <div className="max-w-[80%] rounded-2xl px-3 py-2.5" style={{ background: V, borderBottomRightRadius: 4 }}>
                          <div className="mb-1.5 rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.15)' }}>
                            <div className="h-24 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fff, #e5e3f0)' }}>
                              <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }} className="text-center">
                                <FileText className="h-7 w-7 mx-auto text-slate-400" />
                                <p className="text-[9px] font-mono text-slate-500 mt-1">courrier_CAF.pdf</p>
                              </motion.div>
                            </div>
                          </div>
                          <p className="text-xs text-white">📎 PDF envoyé</p>
                        </div>
                      </motion.div>
                    )}

                    {stage === 'analyzing' && (
                      <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2 px-2 py-3">
                        <div className="flex gap-1">
                          {[0,1,2].map(i => (
                            <motion.div key={i} className="w-2 h-2 rounded-full" style={{ background: V }}
                              animate={{ y: [0, -4, 0] }}
                              transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.12 }} />
                          ))}
                        </div>
                        <span className="text-[11px]" style={{ color: ts }}>Analyse en cours…</span>
                      </motion.div>
                    )}

                    {(stage === 'result' || stage === 'listen' || stage === 'copied') && (
                      <motion.div key="result" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-3 space-y-2.5" style={{ background: bgInner, border: `1px solid ${bdInner}`, borderBottomLeftRadius: 4 }}>
                        {/* Résumé + TTS */}
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className="text-[9px] font-semibold uppercase tracking-wider flex items-center gap-1" style={{ color: V }}>
                              <FileText className="h-2.5 w-2.5" /> Résumé
                            </p>
                            <motion.div
                              className="relative flex items-center gap-1 px-2 py-0.5 rounded-full"
                              style={{ background: stage === 'listen' ? V : 'transparent', border: `1px solid ${stage === 'listen' ? V : bdInner}`, color: stage === 'listen' ? '#fff' : V }}
                              animate={stage === 'listen' ? { scale: [1, 1.05, 1] } : {}}
                              transition={{ duration: 0.6, repeat: stage === 'listen' ? Infinity : 0 }}
                            >
                              {stage === 'listen' && <PulseRing />}
                              <Volume2 className="h-2.5 w-2.5 relative" />
                              <span className="text-[9px] font-semibold relative">Écouter</span>
                            </motion.div>
                          </div>
                          <p className="text-[11px] leading-relaxed font-medium" style={{ color: tp }}>{DEMO_DATA.summary}</p>
                          {stage === 'listen' && (
                            <div className="flex items-end gap-0.5 mt-1.5 h-3">
                              {soundBars.map(i => (
                                <motion.div key={i} className="w-0.5 rounded-full" style={{ background: V }}
                                  animate={{ height: [4, 10, 6, 12, 4] }}
                                  transition={{ duration: 0.7, repeat: Infinity, delay: i * 0.08 }} />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Explication */}
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-0.5" style={{ color: '#2563EB' }}>
                            <ImageIcon className="h-2.5 w-2.5" /> Explication simple
                          </p>
                          <p className="text-[11px] leading-relaxed" style={{ color: tp }}>{DEMO_DATA.explanation}</p>
                        </div>

                        {/* Actions */}
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-0.5" style={{ color: '#059669' }}>
                            <CheckCircle2 className="h-2.5 w-2.5" /> Ce que vous devez faire
                          </p>
                          <ul className="space-y-0.5">
                            {DEMO_DATA.actions.map((a, i) => (
                              <motion.li key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.1 }} className="text-[11px] flex gap-1.5" style={{ color: tp }}>
                                <span style={{ color: '#059669' }}>✓</span>
                                <span>{a}</span>
                              </motion.li>
                            ))}
                          </ul>
                        </div>

                        {/* Letter + buttons */}
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-0.5" style={{ color: '#0EA5E9' }}>
                            <Send className="h-2.5 w-2.5" /> Réponse prête
                          </p>
                          <div className="rounded-md px-2 py-1.5 max-h-16 overflow-hidden" style={{ background: isDark ? 'rgba(0,0,0,0.25)' : '#fff', border: `1px solid ${bd}` }}>
                            <pre className="font-sans text-[10px] whitespace-pre-wrap leading-snug" style={{ color: ts }}>{DEMO_DATA.letter}</pre>
                          </div>
                          <motion.button
                            className="mt-1.5 w-full h-7 rounded-md text-[10px] font-semibold flex items-center justify-center gap-1 text-white"
                            style={{ background: copied ? '#22C55E' : `linear-gradient(135deg, ${V}, #5B4FD9)` }}
                            animate={stage === 'copied' ? { scale: [1, 0.95, 1] } : {}}
                          >
                            {copied ? <><Check className="h-3 w-3" /> Copié</> : <><Copy className="h-3 w-3" /> Copier</>}
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Input bar */}
                <div className="px-3 pb-3 pt-1">
                  <div className="flex items-center gap-1 rounded-lg px-2 h-9" style={{ background: isDark ? '#252538' : '#F5F3FF', border: `1px solid ${bd}` }}>
                    <div className="p-1" style={{ color: V }}><FileText className="h-3 w-3" /></div>
                    <div className="flex-1 text-[10px]" style={{ color: ts }}>Écrivez ou parlez…</div>
                    <div className="p-1" style={{ color: V }}><Mic className="h-3 w-3" /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* MIDDLE: stage indicator (desktop only) */}
          <div className="hidden md:flex flex-col gap-4 px-4">
            {[
              { label: 'Photo / PDF', active: stage === 'upload' },
              { label: 'Analyse IA', active: stage === 'analyzing' },
              { label: 'Résultat', active: stage === 'result' },
              { label: 'Écoute', active: stage === 'listen' },
              { label: 'Copie', active: stage === 'copied' }
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <motion.div
                  className="w-2 h-2 rounded-full shrink-0"
                  animate={{ scale: step.active ? 1.4 : 1, backgroundColor: step.active ? V : (isDark ? '#2E2E45' : '#D4CFFF') }}
                />
                <span className="text-xs font-medium transition-colors" style={{ color: step.active ? tp : ts }}>{step.label}</span>
              </div>
            ))}
          </div>

          {/* RIGHT: benefits */}
          <div className="space-y-3">
            {[
              { icon: FileText, color: V, title: 'Photo, PDF ou texte', desc: 'Prenez votre courrier en photo ou glissez un PDF — notre IA comprend tout.' },
              { icon: Volume2, color: '#2563EB', title: 'Lecture à voix haute', desc: 'Écoutez l\'explication dans votre langue (FR, EN, ES, DE, IT…).' },
              { icon: Mic, color: '#DC2626', title: 'Parlez au lieu d\'écrire', desc: 'Dictée vocale intégrée — idéale en déplacement ou pour les non-francophones.' },
              { icon: CheckCircle2, color: '#059669', title: 'Courrier prêt à envoyer', desc: 'Copie, PDF ou envoi postal — en un clic.' }
            ].map((b, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="flex gap-3 p-3 rounded-xl"
                style={{ background: cardBg, border: `1px solid ${bd}` }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${b.color}15` }}>
                  <b.icon className="h-4 w-4" style={{ color: b.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold mb-0.5" style={{ color: tp }}>{b.title}</p>
                  <p className="text-xs leading-relaxed" style={{ color: ts }}>{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

ChatbotDemo.displayName = 'ChatbotDemo';
export default ChatbotDemo;
